import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Zap, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, X,
  Wallet, Info, ExternalLink, Trash2, RefreshCw, Target, Shield,
  ChevronDown, ChevronUp, Activity,
} from 'lucide-react';
import useTokenPrice from '../hooks/useTokenPrice';

// Symbol → CoinGecko id for live-price lookups
const ST_SYMBOL_TO_CG = {
  WETH: 'ethereum',  ETH: 'ethereum',
  WBTC: 'bitcoin',   BTC: 'bitcoin',  cbBTC: 'bitcoin',  'BTC.b': 'bitcoin',
  WMATIC: 'matic-network', MATIC: 'matic-network', POL: 'matic-network',
  WBNB: 'binancecoin', BNB: 'binancecoin',
  WAVAX: 'avalanche-2', AVAX: 'avalanche-2',
  LINK: 'chainlink', UNI: 'uniswap', AAVE: 'aave',
  ARB: 'arbitrum', OP: 'optimism', SOL: 'solana',
  CRV: 'curve-dao-token', MKR: 'maker', COMP: 'compound-governance-token',
};
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Spinner, InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import {
  prepareDexSmartTrade, commitDexSmartTrade,
  listDexSmartTrades, cancelDexSmartTrade,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor, chartPairFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';

const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];
const MAX_UINT256 = 2n ** 256n - 1n;
const COW_SPENDER = '0xC92E8bdf79f0507f65a392b0ab4667716BFE0110';

const EXPIRY_OPTIONS = [
  { label: '24 hours', sec: 86400 },
  { label: '7 days',   sec: 7 * 86400 },
  { label: '30 days',  sec: 30 * 86400 },
];

const STATUS_STYLE = {
  pending_entry: { label: 'Waiting for entry', cls: 'bg-amber-500/10 text-amber-400', icon: Clock },
  entry_filled:  { label: 'TP/SL Armed',       cls: 'bg-emerald-500/10 text-emerald-400', icon: Shield },
  completed:     { label: 'Completed',         cls: 'bg-blue-500/10 text-blue-300',     icon: CheckCircle },
  cancelled:     { label: 'Cancelled',         cls: 'bg-dark-600 text-gray-400',        icon: X },
  failed:        { label: 'Failed',            cls: 'bg-red-500/10 text-red-400',       icon: AlertTriangle },
};

function toBaseUnits(humanAmount, decimals) {
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('amount must be a non-negative decimal');
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}
function formatBaseUnits(baseStr, decimals, maxFrac = 4) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}
function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}
function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending_entry;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function CreateSmartTradePanel({ onCreated, onToast, onPairChange }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [protocol, setProtocol] = useState('cow');
  const [chainId, setChainId] = useState(connectedChainId || 1);
  const [sellSym, setSellSym] = useState('USDC');
  const [buySym,  setBuySym]  = useState('WETH');
  const [sellAmount, setSellAmount] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [takeProfitPct, setTakeProfitPct] = useState(10);
  const [stopLossPct,   setStopLossPct]   = useState(5);
  const [slippagePct,   setSlippagePct]   = useState(1);
  const [expirySec, setExpirySec] = useState(7 * 86400);
  const [stage, setStage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [customTick, setCustomTick] = useState(0);

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const availableChains = CHAINS.filter(c => PROTOCOL_CHAINS[protocol].includes(c.id));

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  useEffect(() => {
    onPairChange?.({ sellSym, buySym, chainId });
  }, [sellSym, buySym, chainId, onPairChange]);

  useEffect(() => {
    if (!PROTOCOL_CHAINS[protocol].includes(chainId)) setChainId(PROTOCOL_CHAINS[protocol][0]);
  }, [protocol, chainId]);

  const tpTrigger = useMemo(() => {
    const e = Number(entryPrice), tp = Number(takeProfitPct);
    return Number.isFinite(e) && Number.isFinite(tp) && e > 0 ? e * (1 + tp / 100) : null;
  }, [entryPrice, takeProfitPct]);
  const slTrigger = useMemo(() => {
    const e = Number(entryPrice), sl = Number(stopLossPct);
    return Number.isFinite(e) && Number.isFinite(sl) && e > 0 ? e * (1 - sl / 100) : null;
  }, [entryPrice, stopLossPct]);

  const disabledReason = (() => {
    if (!isConnected || !connectedAddr) return 'Connect your wallet first';
    if (Number(connectedChainId) !== Number(chainId))
      return `Wallet is on chain ${connectedChainId}, not ${CHAINS.find(c => c.id === Number(chainId))?.label || chainId}`;
    if (!sellToken || !buyToken || sellToken.symbol === buyToken.symbol) return 'Sell and buy tokens must be different';
    if (!(Number(sellAmount) > 0)) return 'Enter a sell amount > 0';
    if (!(Number(entryPrice) > 0)) return 'Enter an entry price > 0';
    if (!(Number(takeProfitPct) > 0)) return 'Take-profit % must be > 0';
    if (!(Number(stopLossPct)   > 0)) return 'Stop-loss % must be > 0';
    if (submitting) return 'Submitting…';
    return null;
  })();
  const canSubmit = disabledReason == null;

  async function ensureAllowance(spender, requiredAmount) {
    setStage('checking-allowance');
    async function read() {
      return publicClient.readContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'allowance', args: [connectedAddr, spender],
      });
    }
    let allowance = await read();
    if (allowance >= requiredAmount) return;
    if (allowance > 0n) {
      setStage('approving');
      onToast({ type: 'info', message: `${sellSym} needs allowance reset to 0 first — confirm in wallet.` });
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await read(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error('Allowance reset did not confirm');
    }
    setStage('approving');
    onToast({ type: 'info', message: `Approve ${sellSym} once. ~15-30s for on-chain confirmation.` });
    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await read(); } catch {}
      if (allowance >= requiredAmount) return;
    }
    throw new Error('Approval did not confirm in 3 min');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const totalSellAmount = toBaseUnits(sellAmount, sellToken.decimals);
      const expiresAt = Math.floor(Date.now() / 1000) + Number(expirySec);

      // 1. Prepare — backend returns 3 typed-data blobs + spender
      setStage('preparing');
      const prep = await prepareDexSmartTrade({
        protocol, chainId,
        owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        totalSellAmount,
        entryPrice: Number(entryPrice),
        takeProfitPct: Number(takeProfitPct),
        stopLossPct:   Number(stopLossPct),
        slippagePct:   Number(slippagePct),
        expiresAt,
      });
      const { smartTradeId, entry, tp, sl, entryBuyAmount, triggers } = prep.data;
      const spender = entry.spender;

      // 2. Approve sell-token allowance (one-time)
      await ensureAllowance(spender, BigInt(totalSellAmount));

      // 3. Sign 3 orders in sequence
      const signed = {};
      for (const [legKey, leg] of [['entry', entry], ['tp', tp], ['sl', sl]]) {
        setStage(`signing-${legKey}`);
        const signature = await signTypedDataAsync({
          domain: leg.typedData.domain,
          types:  leg.typedData.types,
          primaryType: leg.typedData.primaryType,
          message: leg.order,
        });
        signed[legKey] = { order: leg.order, signature, orderId: leg.orderId };
      }

      // 4. Commit
      setStage('committing');
      const result = await commitDexSmartTrade({
        smartTradeId, protocol, chainId, owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        totalSellAmount, entryBuyAmount,
        entryPrice: Number(entryPrice),
        takeProfitPct: Number(takeProfitPct), takeProfitTriggerUsd: triggers.takeProfitTriggerUsd,
        stopLossPct:   Number(stopLossPct),   stopLossTriggerUsd:   triggers.stopLossTriggerUsd,
        slippagePct:   Number(slippagePct),
        expiresAt,
        entry: signed.entry, tp: signed.tp, sl: signed.sl,
      });

      onToast({ type: 'success', message: `Smart Trade created — entry at $${entryPrice}, TP +${takeProfitPct}%, SL -${stopLossPct}%. You'll be notified at each step.` });
      setSellAmount(''); setEntryPrice('');
      onCreated(result.data?.record);
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Could not create smart trade';
      onToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
      setStage(null);
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center">
          <Zap className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New Smart Trade</h3>
          <p className="text-gray-500 text-xs">Entry + take-profit + stop-loss in one signed bundle. Set and forget.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}
      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">Wallet is on chain {connectedChainId}; switch to {CHAINS.find(c => c.id === chainId)?.label}.</p>
          </div>
          <button onClick={() => switchChain?.({ chainId: Number(chainId) })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white disabled:opacity-40">
            {switchingChain ? 'Switching…' : `Switch to ${CHAINS.find(c => c.id === Number(chainId))?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Protocol */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Protocol</label>
          <div className="grid grid-cols-2 gap-2">
            {['cow', 'velora'].map(p => (
              <button key={p} type="button" onClick={() => setProtocol(p)}
                className={`px-3 py-2 rounded-xl text-xs font-medium border ${
                  protocol === p ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                {p === 'cow' ? 'CoW' : 'Velora'}
              </button>
            ))}
          </div>
        </div>

        {/* Chain */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {availableChains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Sell + buy + amount */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Spend</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Amount</label>
            <input type="number" min="0" step="any" value={sellAmount}
              onChange={e => setSellAmount(e.target.value)} placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Buy</label>
            <select value={buySym} onChange={e => setBuySym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
        </div>

        {/* Custom token */}
        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {/* Entry price */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Entry price <span className="text-gray-600">(USD per {buySym})</span>
            <InfoTip side="right">Limit price for the entry leg. Set below current market to "buy the dip". Bundle expires unfilled if market never reaches this price within the watch window.</InfoTip>
          </label>
          <input type="number" min="0" step="any" value={entryPrice}
            onChange={e => setEntryPrice(e.target.value)} placeholder="0.0"
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
        </div>

        {/* TP + SL */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-emerald-400" /> Take-profit %
              <InfoTip side="top">Sells at entry × (1 + tp%). Typical: 10-20% retail, 5% day-trade, 50%+ long-hold. Preview line shows the exact USD trigger.</InfoTip>
            </label>
            <input type="number" min="0.5" max="200" step="0.5" value={takeProfitPct}
              onChange={e => setTakeProfitPct(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary" />
            {tpTrigger && <p className="text-emerald-400/80 text-[10px] mt-1">→ Sells at ${tpTrigger.toFixed(2)}</p>}
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <TrendingDown className="w-3 h-3 text-red-400" /> Stop-loss %
              <InfoTip side="top">Sells at entry × (1 - sl%). Typical: 3-5% tight, 10% loose. When SL fires, TP auto-cancels (OCO behavior).</InfoTip>
            </label>
            <input type="number" min="0.5" max="200" step="0.5" value={stopLossPct}
              onChange={e => setStopLossPct(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary" />
            {slTrigger && <p className="text-red-400/80 text-[10px] mt-1">→ Sells at ${slTrigger.toFixed(2)}</p>}
          </div>
        </div>

        {/* Slippage + expiry */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Slippage</label>
            <input type="number" min="0.1" max="10" step="0.1" value={slippagePct}
              onChange={e => setSlippagePct(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide block mb-1.5">Watch for</label>
            <select value={expirySec} onChange={e => setExpirySec(Number(e.target.value))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {EXPIRY_OPTIONS.map(o => <option key={o.sec} value={o.sec}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={!canSubmit}
          className="w-full btn-primary py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
          {submitting ? <Spinner size="sm" /> : <Zap className="w-4 h-4" />}
          {stage === 'preparing' ? 'Preparing 3 legs…' :
           stage === 'checking-allowance' ? 'Checking allowance…' :
           stage === 'approving' ? `Approving ${sellSym}…` :
           stage === 'signing-entry' ? 'Sign 1 of 3 — Entry order…' :
           stage === 'signing-tp' ? 'Sign 2 of 3 — Take-profit…' :
           stage === 'signing-sl' ? 'Sign 3 of 3 — Stop-loss…' :
           stage === 'committing' ? 'Saving smart trade…' :
           'Arm Smart Trade'}
        </button>
        {disabledReason ? (
          <p className="text-amber-400 text-[11px] text-center flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {disabledReason}
          </p>
        ) : (
          <p className="text-gray-500 text-[11px] text-center flex items-center justify-center gap-1">
            3 wallet signatures total (entry + TP + SL). Then everything is autonomous.
            <InfoTip side="top">Plus a one-time ERC-20 approval if this is your first time spending this token on this protocol. After all 4 signatures, the bundle runs without further input. Cancel cancels all 3 legs at once.</InfoTip>
          </p>
        )}
      </div>
    </Card>
  );
}

function SmartTradeRow({ record, onCancel, busy }) {
  const [expanded, setExpanded] = useState(false);
  const protoLabel = record.protocol === 'cow' ? 'CoW' : 'Velora';

  // Live price of the BUY token (the asset we're holding once entry fills —
  // it's what the TP/SL legs watch).
  const cgId = ST_SYMBOL_TO_CG[record.buySymbol] || null;
  const { data: liveData } = useTokenPrice(cgId, 30000);
  const livePrice = liveData?.currentPriceUsd ?? liveData?.market_data?.current_price?.usd ?? null;

  // Distance helpers
  function dist(target) {
    if (!Number.isFinite(livePrice) || !Number.isFinite(target)) return null;
    const usd = livePrice - target;
    const pct = (usd / target) * 100;
    return { usd, pct };
  }
  const dEntry = dist(record.entryPrice);
  const dTp    = dist(record.takeProfitTriggerUsd);
  const dSl    = dist(record.stopLossTriggerUsd);

  function copyToClipboard(text) { try { navigator.clipboard?.writeText(text); } catch {} }

  // Leg state derivation
  function legState(legKey) {
    if (legKey === 'entry') {
      if (record.status === 'pending_entry')                return { label: 'waiting',  cls: 'text-amber-400' };
      if (record.entryFilledAt)                              return { label: 'filled',   cls: 'text-emerald-400' };
      if (record.status === 'cancelled')                     return { label: 'cancelled', cls: 'text-gray-500' };
      return { label: '—', cls: 'text-gray-500' };
    }
    if (legKey === 'tp') {
      if (record.status === 'pending_entry')                return { label: 'pending entry', cls: 'text-gray-500' };
      if (record.tpFilledAt)                                 return { label: 'filled',        cls: 'text-emerald-400' };
      if (record.status === 'entry_filled')                  return { label: 'armed',         cls: 'text-emerald-400' };
      if (record.completedLeg === 'sl')                      return { label: 'cancelled (SL fired)', cls: 'text-gray-500' };
      if (record.status === 'cancelled')                     return { label: 'cancelled',     cls: 'text-gray-500' };
      return { label: '—', cls: 'text-gray-500' };
    }
    if (legKey === 'sl') {
      if (record.status === 'pending_entry')                return { label: 'pending entry', cls: 'text-gray-500' };
      if (record.slFilledAt)                                 return { label: 'filled',        cls: 'text-red-400' };
      if (record.status === 'entry_filled')                  return { label: 'armed',         cls: 'text-red-400' };
      if (record.completedLeg === 'tp')                      return { label: 'cancelled (TP fired)', cls: 'text-gray-500' };
      if (record.status === 'cancelled')                     return { label: 'cancelled',     cls: 'text-gray-500' };
      return { label: '—', cls: 'text-gray-500' };
    }
  }
  const entryState = legState('entry');
  const tpState    = legState('tp');
  const slState    = legState('sl');

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Zap className="w-3.5 h-3.5 text-accent-primary" />
              <span className="text-white text-sm font-semibold">
                {formatBaseUnits(record.totalSellAmount, record.sellDecimals)} {record.sellSymbol} → {record.buySymbol}
              </span>
              <StatusBadge status={record.status} />
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">{protoLabel}</span>
            </div>
            <p className="text-gray-400 text-xs mt-1">
              Entry: <span className="text-white">${record.entryPrice}</span> · TP: <span className="text-emerald-400">+{record.takeProfitPct}% (${record.takeProfitTriggerUsd.toFixed(2)})</span> · SL: <span className="text-red-400">-{record.stopLossPct}% (${record.stopLossTriggerUsd.toFixed(2)})</span>
            </p>
            {record.status === 'pending_entry' && (
              <p className="text-gray-500 text-xs mt-0.5">Waiting for entry order to fill at ${record.entryPrice}…</p>
            )}
            {record.status === 'entry_filled' && (
              <p className="text-emerald-400 text-xs mt-0.5">Entry filled {fmtTime(record.entryFilledAt)} · TP and SL armed</p>
            )}
            {record.status === 'completed' && (
              <p className="text-blue-300 text-xs mt-0.5">
                {record.completedLeg === 'tp' ? '✓ Take-profit fired' : '⚠️ Stop-loss fired'}
                {record.tpFilledAt && ` at ${fmtTime(record.tpFilledAt)}`}
                {record.slFilledAt && ` at ${fmtTime(record.slFilledAt)}`}
              </p>
            )}
            {record.cancelledReason && (
              <p className="text-gray-500 text-xs mt-0.5">Reason: {record.cancelledReason}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {(record.status === 'pending_entry' || record.status === 'entry_filled') && (
              <button onClick={() => onCancel(record.smartTradeId)} disabled={busy}
                title="Cancel all 3 legs"
                className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="ml-1 text-gray-500" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[st-fade_0.2s_ease-out]">
          <style>{`@keyframes st-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          {/* Live price snapshot */}
          {cgId && (
            <div className="bg-gradient-to-r from-dark-700/50 to-dark-700/20 rounded-lg px-3 py-2.5 border border-dark-600">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
                    <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
                    Live {record.buySymbol} price
                  </div>
                  <div className="text-base font-mono font-semibold text-white mt-0.5">
                    {livePrice != null ? `$${Number(livePrice).toFixed(livePrice >= 1 ? 2 : 4)}` : '—'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 3-leg state table */}
          <div className="bg-dark-700/30 rounded-xl border border-dark-600 overflow-hidden">
            <div className="grid grid-cols-12 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold bg-dark-800/60">
              <div className="col-span-2">Leg</div>
              <div className="col-span-3">Price</div>
              <div className="col-span-3">State</div>
              <div className="col-span-4 text-right">Distance from now</div>
            </div>
            {/* Entry */}
            <div className="grid grid-cols-12 px-3 py-1.5 text-xs border-b border-dark-600/30 hover:bg-dark-600/20">
              <div className="col-span-2 text-white font-semibold">Entry</div>
              <div className="col-span-3 text-white font-mono">${Number(record.entryPrice).toFixed(2)}</div>
              <div className={`col-span-3 ${entryState.cls}`}>{entryState.label}</div>
              <div className="col-span-4 text-right font-mono text-gray-300">
                {dEntry ? (
                  <>{dEntry.usd > 0 ? '+' : ''}${Math.abs(dEntry.usd).toFixed(2)} <span className="text-gray-500">({dEntry.pct > 0 ? '+' : ''}{dEntry.pct.toFixed(2)}%)</span></>
                ) : '—'}
              </div>
            </div>
            {/* TP */}
            <div className="grid grid-cols-12 px-3 py-1.5 text-xs border-b border-dark-600/30 hover:bg-dark-600/20">
              <div className="col-span-2 text-emerald-400 font-semibold">Take-profit</div>
              <div className="col-span-3 text-white font-mono">${Number(record.takeProfitTriggerUsd).toFixed(2)}</div>
              <div className={`col-span-3 ${tpState.cls}`}>{tpState.label}</div>
              <div className="col-span-4 text-right font-mono text-gray-300">
                {dTp ? (
                  <>{dTp.usd > 0 ? '+' : ''}${Math.abs(dTp.usd).toFixed(2)} <span className="text-gray-500">({dTp.pct > 0 ? '+' : ''}{dTp.pct.toFixed(2)}%)</span></>
                ) : '—'}
              </div>
            </div>
            {/* SL */}
            <div className="grid grid-cols-12 px-3 py-1.5 text-xs hover:bg-dark-600/20">
              <div className="col-span-2 text-red-400 font-semibold">Stop-loss</div>
              <div className="col-span-3 text-white font-mono">${Number(record.stopLossTriggerUsd).toFixed(2)}</div>
              <div className={`col-span-3 ${slState.cls}`}>{slState.label}</div>
              <div className="col-span-4 text-right font-mono text-gray-300">
                {dSl ? (
                  <>{dSl.usd > 0 ? '+' : ''}${Math.abs(dSl.usd).toFixed(2)} <span className="text-gray-500">({dSl.pct > 0 ? '+' : ''}{dSl.pct.toFixed(2)}%)</span></>
                ) : '—'}
              </div>
            </div>
          </div>

          {/* Linked IDs */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2 space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Smart trade ID</div>
              <button onClick={() => copyToClipboard(record.smartTradeId)} title="Copy"
                className="text-xs font-mono text-white break-all hover:text-accent-primary transition-colors text-left">
                {record.smartTradeId}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-1">
              {[
                ['Entry order',  record.entryOrderId],
                ['TP stop order',record.tpStopOrderId],
                ['SL stop order',record.slStopOrderId],
              ].map(([label, id]) => id && (
                <div key={label}>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
                  <button onClick={() => copyToClipboard(id)} title="Copy"
                    className="text-[10px] font-mono text-gray-300 hover:text-accent-primary transition-colors text-left truncate w-full">
                    {String(id).slice(0, 8)}…{String(id).slice(-6)}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Lifecycle */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Lifecycle</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-white">{fmtTime(record.createdAt)}</span></div>
              {record.entryFilledAt && (
                <div className="flex justify-between"><span className="text-emerald-400">Entry filled</span><span className="text-white">{fmtTime(record.entryFilledAt)}</span></div>
              )}
              {record.tpFilledAt && (
                <div className="flex justify-between"><span className="text-emerald-400">TP fired</span><span className="text-white">{fmtTime(record.tpFilledAt)}</span></div>
              )}
              {record.slFilledAt && (
                <div className="flex justify-between"><span className="text-red-400">SL fired</span><span className="text-white">{fmtTime(record.slFilledAt)}</span></div>
              )}
              {record.cancelledAt && (
                <div className="flex justify-between"><span className="text-gray-500">Cancelled</span><span className="text-white">{fmtTime(record.cancelledAt)}</span></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DexSmartTradePage() {
  const [trades, setTrades]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState(null);
  const [busy, setBusy]       = useState(null);
  const [chartPair, setChartPair] = useState('ETH/USDT');

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await listDexSmartTrades(); setTrades(r.data?.trades || []); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Could not load smart trades' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const handleCreated = (record) => {
    if (record) setTrades(prev => [record, ...prev.filter(t => t.smartTradeId !== record.smartTradeId)]);
  };
  const handleCancel = async (id) => {
    setBusy(id);
    try {
      const r = await cancelDexSmartTrade(id);
      setTrades(prev => prev.map(t => t.smartTradeId === id ? r.data?.record || t : t));
      setToast({ type: 'success', message: 'Smart Trade cancelled. All 3 legs stopped.' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Cancel failed' });
    } finally { setBusy(null); }
  };

  const active = trades.filter(t => t.status === 'pending_entry' || t.status === 'entry_filled');
  const history = trades.filter(t => !['pending_entry', 'entry_filled'].includes(t.status));

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-accent-primary/15 text-accent-primary flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX Smart Trade</h1>
            <p className="text-gray-400 text-sm">Entry + take-profit + stop-loss in one signed bundle. Composed over CoW/Velora + Chainlink — set and forget.</p>
          </div>
        </div>
      </div>

      <Card className="!p-3">
        <div className="flex items-center justify-between mb-2 px-2 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs uppercase tracking-wide">Reference chart</span>
            <span className="text-white text-sm font-mono">{chartPair}</span>
          </div>
          <span className="text-[10px] text-gray-500">Powered by TradingView</span>
        </div>
        <TradingViewChart symbol={chartPair} interval="60" height={320} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateSmartTradePanel onCreated={handleCreated} onToast={setToast}
          onPairChange={({ sellSym, buySym }) => setChartPair(chartPairFor(sellSym, buySym))} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Active smart trades</h3>
              <p className="text-gray-500 text-xs">{active.length} running</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Target className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No active smart trades. Create one with TP + SL in a single signed bundle.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(t => <SmartTradeRow key={t.smartTradeId} record={t} onCancel={handleCancel} busy={busy === t.smartTradeId} />)}
            </div>
          )}
        </Card>
      </div>

      {history.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">History</h3>
          <div className="space-y-2">
            {history.map(t => <SmartTradeRow key={t.smartTradeId} record={t} onCancel={handleCancel} busy={busy === t.smartTradeId} />)}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
